import React, { useState, useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import { QuickCapture } from './components/QuickCapture';
import { EnhancedVoiceRecorder } from './components/EnhancedVoiceRecorder';
import { IdeaList } from './components/IdeaList';
import { IdeaDetail } from './components/IdeaDetail';
import { SearchBar, SearchFilters } from './components/SearchBar';
import { Settings } from './components/Settings';
import { PWAInstallPrompt } from './components/PWAInstallPrompt';
import { SyncIndicator } from './components/SyncIndicator';
import { ActionItems } from './components/ActionItems';
import Login from './components/Login';
import { ideaApi, Idea, projectApi, Project, authApi, SearchParams } from './services/api';
import { useSync } from './hooks/useSync';
import './App.css';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedIdea, setSelectedIdea] = useState<Idea | null>(null);
  const [activeTab, setActiveTab] = useState<'capture' | 'ideas' | 'projects' | 'actions' | 'settings'>('capture');
  const [isLoading, setIsLoading] = useState(false);
  const [searchFilters, setSearchFilters] = useState<SearchFilters>({ query: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [totalIdeas, setTotalIdeas] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);
  
  // Real-time sync hook
  const { subscribeToIdeas, subscribeToProjects } = useSync();

  // Check if user is already authenticated
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('authToken');
      if (token) {
        try {
          await authApi.verify();
          setIsAuthenticated(true);
        } catch {
          localStorage.removeItem('authToken');
        }
      }
      setIsCheckingAuth(false);
    };
    checkAuth();

    // Register service worker for PWA
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js')
        .then((registration) => {
          console.log('SW registered:', registration);
        })
        .catch((error) => {
          console.log('SW registration failed:', error);
        });
    }

    // Handle PWA shortcuts from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const action = urlParams.get('action');
    if (action === 'capture') {
      setActiveTab('capture');
    } else if (action === 'voice') {
      setActiveTab('capture');
      // Focus will be handled by the VoiceInput component
    }

    // Global keyboard shortcuts
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ctrl+Shift+B for quick capture
      if (event.ctrlKey && event.shiftKey && event.key === 'B') {
        event.preventDefault();
        setActiveTab('capture');
        // Focus the quick capture input
        setTimeout(() => {
          const input = document.querySelector('textarea[placeholder*="idea"]') as HTMLTextAreaElement;
          if (input) {
            input.focus();
          }
        }, 100);
      }
      // Ctrl+Shift+V for voice capture
      else if (event.ctrlKey && event.shiftKey && event.key === 'V') {
        event.preventDefault();
        setActiveTab('capture');
        // Trigger voice input
        setTimeout(() => {
          const voiceButton = document.querySelector('button[title*="voice"]') as HTMLButtonElement;
          if (voiceButton) {
            voiceButton.click();
          }
        }, 100);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const fetchIdeas = async (filters: SearchFilters = { query: '' }, page: number = 1, append: boolean = false) => {
    setIsLoading(true);
    try {
      if ((!filters.query && Object.keys(filters).filter(k => k !== 'query').every(k => !filters[k as keyof SearchFilters])) && page === 1) {
        // Use simple getAll for initial load
        const fetchedIdeas = await ideaApi.getAll();
        setIdeas(fetchedIdeas);
        setTotalIdeas(fetchedIdeas.length);
        setHasMore(false);
      } else {
        // Use search API with filters
        const searchParams: SearchParams = {
          ...filters,
          page,
          limit: 20
        };
        const response = await ideaApi.search(searchParams);
        
        if (append) {
          setIdeas(prev => [...prev, ...response.ideas]);
        } else {
          setIdeas(response.ideas);
        }
        
        setTotalIdeas(response.total);
        setHasMore(page < response.totalPages);
        setCurrentPage(page);
      }
    } catch (error) {
      console.error('Failed to fetch ideas:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const cats = await ideaApi.getCategories();
      setCategories(cats);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchProjects = async () => {
    try {
      const fetchedProjects = await projectApi.getAll();
      setProjects(fetchedProjects);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchIdeas();
      fetchProjects();
      fetchCategories();
      
      const interval = setInterval(() => {
        fetchIdeas(searchFilters, currentPage);
        fetchProjects();
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  // Subscribe to real-time updates
  useEffect(() => {
    if (!isAuthenticated) return;

    const unsubscribe = subscribeToIdeas(
      // On idea created
      (newIdea: Idea) => {
        setIdeas(prev => [newIdea, ...prev]);
        setTotalIdeas(prev => prev + 1);
      },
      // On idea updated
      (updatedIdea: Idea) => {
        setIdeas(prev => prev.map(idea => 
          idea.id === updatedIdea.id ? updatedIdea : idea
        ));
        // Update selected idea if it's the one being viewed
        if (selectedIdea?.id === updatedIdea.id) {
          setSelectedIdea(updatedIdea);
        }
      },
      // On idea deleted
      (deletedId: string) => {
        setIdeas(prev => prev.filter(idea => idea.id !== parseInt(deletedId)));
        setTotalIdeas(prev => prev - 1);
        // Close detail view if the deleted idea was selected
        if (selectedIdea?.id === parseInt(deletedId)) {
          setSelectedIdea(null);
        }
      }
    );

    const unsubscribeProjects = subscribeToProjects((newProject: Project) => {
      setProjects(prev => [newProject, ...prev]);
    });

    return () => {
      unsubscribe();
      unsubscribeProjects();
    };
  }, [isAuthenticated, subscribeToIdeas, subscribeToProjects, selectedIdea]);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    setIsAuthenticated(false);
    setIdeas([]);
    setProjects([]);
  };

  const handleSearch = (filters: SearchFilters) => {
    setSearchFilters(filters);
    setSearchQuery(filters.query || '');
    setCurrentPage(1);
    fetchIdeas(filters, 1);
  };

  const handleClearSearch = () => {
    setSearchFilters({ query: '' });
    setSearchQuery('');
    setCurrentPage(1);
    fetchIdeas();
  };

  const handleLoadMore = () => {
    fetchIdeas(searchFilters, currentPage + 1, true);
  };

  const handleExport = async (format: 'markdown' | 'json') => {
    try {
      const blob = format === 'markdown' 
        ? await ideaApi.exportToMarkdown(searchFilters)
        : await ideaApi.exportToJson(searchFilters);
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `brain-dump-export-${new Date().toISOString().split('T')[0]}.${format === 'markdown' ? 'md' : 'json'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Failed to export:', error);
    }
  };

  if (isCheckingAuth) {
    return <div className="App" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <div>Loading...</div>
    </div>;
  }

  if (!isAuthenticated) {
    return <Login onLogin={() => setIsAuthenticated(true)} />;
  }

  return (
    <div className="App">
      <Toaster position="top-right" />
      
      <header className="app-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <div>
            <h1>Brain Dump</h1>
            <p>Capture ideas quickly, let AI organize them for you</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <SyncIndicator />
            <button
            onClick={handleLogout}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: 'transparent',
              color: '#ffffff',
              border: '1px solid #4d4d4d',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            Logout
          </button>
        </div>
        </div>
      </header>

      <nav className="app-nav">
        <button
          className={`nav-button ${activeTab === 'capture' ? 'active' : ''}`}
          onClick={() => setActiveTab('capture')}
        >
          Capture
        </button>
        <button
          className={`nav-button ${activeTab === 'ideas' ? 'active' : ''}`}
          onClick={() => setActiveTab('ideas')}
        >
          Ideas ({ideas.length})
        </button>
        <button
          className={`nav-button ${activeTab === 'projects' ? 'active' : ''}`}
          onClick={() => setActiveTab('projects')}
        >
          Projects ({projects.length})
        </button>
        <button
          className={`nav-button ${activeTab === 'actions' ? 'active' : ''}`}
          onClick={() => setActiveTab('actions')}
        >
          Actions
        </button>
        <button
          className={`nav-button ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          Settings
        </button>
      </nav>

      <main className="app-main">
        {activeTab === 'capture' && (
          <div className="capture-section">
            <PWAInstallPrompt />
            <div className="capture-methods">
              <div className="method-section">
                <h2>Quick Text Capture</h2>
                <QuickCapture onIdeaCreated={() => {
                  handleClearSearch();
                  fetchIdeas();
                }} />
                <div className="keyboard-hint">
                  💡 Tip: Press Ctrl+Shift+B anywhere to quickly capture an idea
                </div>
              </div>
              
              <div className="method-section">
                <h2>Voice Note</h2>
                <EnhancedVoiceRecorder onIdeaCreated={() => {
                  handleClearSearch();
                  fetchIdeas();
                }} />
                <div className="keyboard-hint">
                  💡 Tip: Press Ctrl+Shift+V anywhere for voice capture
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'ideas' && (
          <div className="ideas-section">
            <div className="ideas-header">
              <h2>Your Ideas</h2>
              <div className="export-buttons">
                <button 
                  className="export-button"
                  onClick={() => handleExport('markdown')}
                >
                  Export to Markdown
                </button>
                <button 
                  className="export-button"
                  onClick={() => handleExport('json')}
                >
                  Export to JSON
                </button>
              </div>
            </div>
            
            <SearchBar
              onSearch={handleSearch}
              onClear={handleClearSearch}
              categories={categories}
              totalResults={totalIdeas}
            />
            
            <IdeaList 
              ideas={ideas} 
              onIdeaClick={setSelectedIdea}
              searchQuery={searchQuery}
              totalCount={totalIdeas}
              hasMore={hasMore}
              onLoadMore={handleLoadMore}
              isLoading={isLoading}
            />
          </div>
        )}

        {activeTab === 'projects' && (
          <div className="projects-section">
            <h2>Generated Projects</h2>
            {projects.length === 0 ? (
              <div className="empty-state">
                <p>No projects yet. High-priority ideas will automatically generate projects.</p>
              </div>
            ) : (
              <div className="project-list">
                {projects.map((project) => (
                  <div key={project.id} className="project-item">
                    <h3>{project.name}</h3>
                    <p>{project.description}</p>
                    <div className="project-meta">
                      <span>Priority: {project.priority}/10</span>
                      <span>Status: {project.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'actions' && (
          <div className="actions-section">
            <ActionItems />
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="settings-section">
            <Settings onClose={() => setActiveTab('capture')} />
          </div>
        )}
      </main>

      {selectedIdea && (
        <div className="modal-overlay" onClick={() => setSelectedIdea(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <IdeaDetail
              idea={selectedIdea}
              onClose={() => setSelectedIdea(null)}
              onUpdate={() => fetchIdeas(searchFilters, currentPage)}
              onIdeaClick={(idea) => setSelectedIdea(idea)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default App;